import React, { useEffect, useMemo, useState } from "react";
import Success from "../../../../formHelper/success";
import apiRequestCall from "../../../../apiHelper/globalHelper";
import { all_routes } from "../../../../router/backendComponents/admin/all_routes";
import apiFileRequestCall from "../../../../apiHelper/filleHelper";

type Id = string;
const uid = () => Math.random().toString(36).slice(2);

interface FileRow {
  id: Id;
  name: string;
  amount: number | "";
  file?: File | null;
  fileName?: string | null;
  fileUrl?: string | null;
  previewUrl?: string | null;
}

type PerRowError = { [field: string]: string | undefined };
type FormError = {
  file_upload?: { [index: number]: PerRowError };
} & { [key: string]: string | undefined };

interface Props {
  onNext?: () => void;
  onPrev?: () => void;
}

const useIsMobile = (maxWidth = 767.98) => {
  const [isMobile, setIsMobile] = useState<boolean>(
    typeof window !== "undefined" ? window.innerWidth <= maxWidth : false
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(`(max-width: ${maxWidth}px)`);
    const handler = (e: MediaQueryListEvent | MediaQueryList) =>
      setIsMobile("matches" in e ? e.matches : mq.matches);
    if (typeof mq.addEventListener === "function")
      mq.addEventListener("change", handler as any);
    else (mq as any).addListener(handler);
    setIsMobile(mq.matches);
    return () => {
      if (typeof mq.removeEventListener === "function")
        mq.removeEventListener("change", handler as any);
      else (mq as any).removeListener(handler);
    };
  }, [maxWidth]);
  return isMobile;
};

export default function BillPayment({ onNext, onPrev }: Props): JSX.Element {
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showMessage, setShowMessage] = useState("");
  const backendRoute = all_routes.backend;
  const [formError, setFormError] = useState<FormError>({});

  const [form, setForm] = useState({
    id: "",
    expanses_company_detail_id: "",
    advancePayment: "",
    advanceDetails: "",
    file_upload: [
      {
        id: uid(),
        name: "",
        amount: "",
        file: null,
        fileName: null,
        fileUrl: null,
        previewUrl: null,
      } as FileRow,
    ],
  });

  /* helpers */
  const addRow = (empty: FileRow) =>
    setForm((p) => ({ ...p, file_upload: [...p.file_upload, empty] }));

  const removeRow = (id: Id) =>
    setForm((p) => ({
      ...p,
      file_upload:
        p.file_upload.length > 1
          ? p.file_upload.filter((x) => x.id !== id)
          : p.file_upload,
    }));

  const setRow = (id: Id, patch: Partial<FileRow>) =>
    setForm((p) => ({
      ...p,
      file_upload: p.file_upload.map((r) =>
        r.id === id ? { ...r, ...patch } : r
      ),
    }));

  const totals = useMemo(() => {
    const sum = form.file_upload.reduce(
      (acc, r) => acc + (Number(r.amount) || 0),
      0
    );
    return { grand: sum };
  }, [form.file_upload]);

  const populateFromPayload = (payload: any) => {
    if (!payload) return;
    const p = payload?.data?.payment_bill ?? payload;
    const rows: FileRow[] =
      Array.isArray(p.uploaded_file) && p.uploaded_file.length
        ? p.uploaded_file.map((r: any) => ({
            id: r.rowId ?? uid(),
            name: r.name ?? "",
            amount: r.amount == null ? "" : Number(r.amount),
            file: null,
            fileName:
              r.fileName ?? (r.fileUrl ? r.fileUrl.split("/").pop() : null),
            fileUrl: r.fileUrl ?? null,
            previewUrl: null,
          }))
        : [
            {
              id: uid(),
              name: "",
              amount: "",
              file: null,
              fileName: null,
              fileUrl: null,
              previewUrl: null,
            },
          ];

    setForm({
      id: p?.id ?? "",
      expanses_company_detail_id: payload?.data?.id ?? "",
      advancePayment: p.advance_payment ?? "",
      advanceDetails: p.advance_details ?? "",
      file_upload: rows,
    });
  };

  const tryLoadCmp = async () => {
    try {
      const res = await apiRequestCall("post", backendRoute.getExpansesDetails);
      if (res?.data) populateFromPayload(res.data);
    } catch (err) {
      console.error("Failed to load details:", err);
    }
  };

  useEffect(() => {
    tryLoadCmp();
  }, []);

  /* file handling */
  const onFileChange = (rowId: Id, file: File | null) => {
    const row = form.file_upload.find((r) => r.id === rowId);
    if (row?.previewUrl) {
      try {
        URL.revokeObjectURL(row.previewUrl);
      } catch {}
    }

    setRow(
      rowId,
      file
        ? {
            file,
            fileName: file.name,
            fileUrl: null,
            previewUrl:
              file.type.startsWith("image/") || file.type === "application/pdf"
                ? URL.createObjectURL(file)
                : null,
          }
        : { file: null, fileName: null, previewUrl: null }
    );
  };

  const removeLocalFile = (rowId: Id) => {
    const row = form.file_upload.find((r) => r.id === rowId);
    if (row?.previewUrl) {
      try {
        URL.revokeObjectURL(row.previewUrl);
      } catch {}
    }
    setRow(rowId, { file: null, fileName: null, previewUrl: null });
  };

  const removeExistingFile = (rowId: Id) => {
    setRow(rowId, { fileUrl: null, fileName: null });
  };

  useEffect(() => {
    return () => {
      form.file_upload.forEach((r) => {
        if (r.previewUrl) {
          try {
            URL.revokeObjectURL(r.previewUrl);
          } catch {}
        }
      });
    };
  }, []);

  const capitalize = (s: string) =>
    s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
  const parseErrors = (errors: Record<string, any>): FormError => {
    const result: FormError = {};
    for (const key in errors) {
      const rawVal = errors[key];
      const message = Array.isArray(rawVal) ? rawVal.join(" ") : String(rawVal);
      if (key.startsWith("file_upload")) {
        const parts = key.split(".");
        const index = Number(parts[1]);
        const field = parts[2];
        if (!isNaN(index) && field) {
          result.file_upload ??= {};
          result.file_upload[index] ??= {};
          const readableField = field.replace(/_/g, " ");
          result.file_upload[index][field] = `${capitalize(
            readableField
          )}: ${message}`;
        }
      } else {
        result[key] = message;
      }
    }
    return result;
  };

  const handleSubmit = async () => {
    setLoading(true);
    setFormError({});
    try {
      const fd = new FormData();
      const files: File[] = [];

      const fileUploadPayload = form.file_upload.map((r) => {
        const fileIndex =
          r.file instanceof File ? files.push(r.file) - 1 : null;
        return {
          rowId: r.id,
          name: r.name || null,
          amount: r.amount === "" ? null : Number(r.amount),
          fileIndex,
          existingFileUrl: r.fileUrl ?? null,
        };
      });

      fd.append("advance_payment", form.advancePayment || "");
      fd.append("advance_details", form.advanceDetails || "");
      fd.append("file_upload", JSON.stringify(fileUploadPayload));
      fd.append("totals", JSON.stringify(totals));

      fd.append("id", form.id || "");
      fd.append(
        "expanses_company_detail_id",
        form.expanses_company_detail_id || ""
      );

      files.forEach((f) => fd.append("upload_file[]", f, f.name));

      const res = await apiFileRequestCall(
        "post",
        backendRoute.addUpdateBillPayment,
        { data: fd }
      );

      if (res?.data?.code === 221) {
        setFormError(parseErrors(res?.data?.errors || {}));
        setLoading(false);
        return;
      }

      if (res?.data) populateFromPayload(res.data);
      tryLoadCmp();
      setShowSuccess(true);
      setShowMessage(res?.data?.message || "Saved successfully");
    } catch (e) {
      console.error("Save failed", e);
      alert("Save failed (see console)");
    } finally {
      setLoading(false);
    }
  };

  /* rows render (responsive) */
  const purchaseRows = form.file_upload.map((r, i) => (
    <div
      key={r.id}
      className="row gx-2 align-items-start mb-2"
      style={{ alignItems: "flex-start" }}
    >
      {/* Description */}
      <div className="col-12 col-sm-6" style={{ minWidth: 0 }}>
        <input
          className="form-control"
          placeholder="Description"
          value={r.name}
          onChange={(e) => setRow(r.id, { name: e.target.value })}
        />
        {formError.file_upload?.[i]?.name && (
          <small className="text-danger">{formError.file_upload[i].name}</small>
        )}
      </div>

      {/* Amount */}
      <div className="col-6 col-sm-2 mt-2 mt-sm-0">
        <input
          type="number"
          className="form-control"
          placeholder="Amount"
          min={0}
          value={r.amount ?? ""}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setRow(r.id, {
              amount: e.target.value === "" ? "" : Number(e.target.value),
            })
          }
        />
      </div>

      {/* File controls + preview */}
      <div className="col-6 col-sm-4 mt-2 mt-sm-0">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <label
            style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}
          >
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              style={{ display: "none" }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onFileChange(
                  r.id,
                  e.target.files && e.target.files[0] ? e.target.files[0] : null
                )
              }
            />

            <span
              className="btn btn-warning d-inline-flex align-items-center gap-2 btn-sm"
              style={{ borderRadius: 6, cursor: "pointer", padding: "6px 8px" }}
            >
              {r.fileUrl || r.previewUrl || r.file ? (
                <i className="feather-edit" />
              ) : (
                "Choose File"
              )}
            </span>
          </label>

          {(r.fileUrl || r.previewUrl) && (
            <label
              style={{
                margin: 0,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span
                className="btn btn-danger d-inline-flex align-items-center gap-1 btn-sm"
                style={{
                  borderRadius: 6,
                  cursor: "pointer",
                  padding: "6px 8px",
                }}
                onClick={() => {
                  if (r.file) removeLocalFile(r.id);
                  else if (r.fileUrl) removeExistingFile(r.id);
                  else if (r.previewUrl) removeLocalFile(r.id);
                }}
                title="Remove file"
              >
                <i className="feather-trash" />
              </span>
            </label>
          )}

          {/* preview area: flexible, can shrink to avoid overflow */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              minWidth: 0,
              flex: "1 1 120px",
              maxWidth: 180,
              overflow: "hidden",
            }}
          >
            {r.previewUrl ? (
              // previewUrl exists (usually for local file previews)
              r.file && r.file.type && r.file.type.startsWith("image/") ? (
                <img
                  src={r.previewUrl}
                  alt={r.fileName || ""}
                  style={{
                    maxHeight: 48,
                    maxWidth: 72,
                    width: "auto",
                    objectFit: "cover",
                    borderRadius: 4,
                    flexShrink: 0,
                  }}
                />
              ) : r.file && r.file.type === "application/pdf" ? (
                <a
                  href={r.previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    textDecoration: "none",
                  }}
                  title={r.fileName ?? "PDF"}
                >
                  📄{" "}
                  <span
                    style={{ overflow: "hidden", textOverflow: "ellipsis" }}
                  >
                    {r.fileName}
                  </span>
                </a>
              ) : (
                <a
                  href={r.previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-block",
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {r.fileName}
                </a>
              )
            ) : r.fileUrl ? (
              // existing file on server (no local File object). decide by extension or filename.
              /\.(jpe?g|png|gif|webp|svg)$/i.test(
                (r.fileName || r.fileUrl || "").toString()
              ) ? (
                <a
                  href={r.fileUrl}
                  target="_blank"
                  download={r.fileName ?? undefined}
                  rel="noopener noreferrer"
                  style={{ display: "flex", alignItems: "center", gap: 8 }}
                  title={r.fileName ?? "Image"}
                >
                  <img
                    src={r.fileUrl}
                    alt={r.fileName || ""}
                    style={{
                      maxHeight: 48,
                      maxWidth: 72,
                      width: "auto",
                      objectFit: "cover",
                      borderRadius: 4,
                      cursor: "pointer",
                      flexShrink: 0,
                    }}
                  />
                </a>
              ) : /\.pdf$/i.test((r.fileName || r.fileUrl || "").toString()) ? (
                <a
                  href={r.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={r.fileName ?? undefined}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    textDecoration: "none",
                  }}
                  title={r.fileName ?? "PDF"}
                >
                  📄{" "}
                  <span
                    style={{ overflow: "hidden", textOverflow: "ellipsis" }}
                  >
                    {r.fileName ?? r.fileUrl.split("/").pop()}
                  </span>
                </a>
              ) : (
                // fallback for unknown server-side files
                <a
                  href={r.fileUrl}
                  target="_blank"
                  download={r.fileName ?? undefined}
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-block",
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {r.fileName ?? r.fileUrl.split("/").pop()}
                </a>
              )
            ) : (
              <small className="text-muted">No file</small>
            )}
          </div>

          {/* row actions (kept inline, small) */}
          <div
            style={{
              display: "flex",
              gap: 6,
              alignItems: "center",
              marginLeft: 0,
            }}
          >
            <button
              type="button"
              className="btn btn-icon btn-sm btn-soft-danger"
              onClick={() => removeRow(r.id)}
              title="Remove row"
            >
              <i className="feather-trash" />
            </button>

            {i === form.file_upload.length - 1 && (
              <button
                type="button"
                className="btn btn-icon btn-sm btn-soft-info"
                onClick={() =>
                  addRow({
                    id: uid(),
                    name: "",
                    amount: "",
                    file: null,
                    fileName: null,
                    fileUrl: null,
                    previewUrl: null,
                  })
                }
                title="Add row"
              >
                <i className="feather-plus" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  ));

  return (
    <div className="supplier-table-root mt-3">
      <style>{`
        /* compact button sizing */

        /* reduce card padding on small screens */
        @media (max-width: 576px) {
          .supplier-table-root { padding: 8px; }
          .supplier-table-root .rt-card input.form-control, .supplier-table-root .rt-card textarea.form-control { font-size: 14px; padding: 8px; }
        }

        /* prevent overflow from long filenames */
        .supplier-table-root a small { word-break: break-word; }
      `}</style>

      <div className="rt-card mb-3 ">
        <small>
          <label style={{ fontWeight: 600 }}>
            Advance Payment <small className="text-muted">(if any)</small>
          </label>
        </small>
        <input
          className="form-control mb-2"
          placeholder="Advance amount / notes"
          value={form.advancePayment}
          onChange={(e) =>
            setForm((p) => ({ ...p, advancePayment: e.target.value }))
          }
        />
        <small>
          <label style={{ fontWeight: 600 }}>Advance Details</label>
        </small>
        <textarea
          className="form-control"
          placeholder="Advance details (optional)"
          style={{ minHeight: 80 }}
          value={form.advanceDetails}
          onChange={(e) =>
            setForm((p) => ({ ...p, advanceDetails: e.target.value }))
          }
        />
      </div>

      <div className="rt-card mb-3">
        <small>
          <label style={{ fontWeight: 600, display: "block", marginBottom: 8 }}>
            Upload Bills
          </label>
        </small>
        {purchaseRows}
      </div>

      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: 12,
          flexWrap: "wrap",
        }}
      >
        <div style={{ color: "red", flex: 1, minWidth: 0 }}></div>
        <div
          style={{
            width: isMobile ? "100%" : 320,
            minWidth: 0,
            border: "1px solid var(--rt-border-color, rgba(0,0,0,0.06))",
            borderRadius: 6,
          }}
        >
          <div
            style={{
              border: "1px solid var(--rt-border-color)",
              borderRadius: 6,
              padding: 10,
              background: "var(--rt-card-bg)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                borderTop: "1px solid var(--rt-border-color)",
                paddingTop: 8,
                marginTop: 8,
              }}
            >
              <h5>
                <span>Total</span>
              </h5>
              <strong>₹{totals.grand.toFixed(2)}</strong>
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          gap: 8,
          justifyContent: "flex-end",
          marginTop: 12,
        }}
      >
        <button type="button" className="btn btn-soft-warning" onClick={onPrev}>
          <i className="ti ti-arrow-left mb-1" /> Back
        </button>
        <button
          type="button"
          className="btn btn-soft-success"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            "Saving..."
          ) : (
            <>
              Save & Next <i className="ti ti-arrow-right" />
            </>
          )}
        </button>
      </div>

      <Success
        id="OwnerSuccessModal"
        title="Bill Payment"
        message={showMessage}
        show={showSuccess}
        onClose={() => {
          setShowSuccess(false);
          try {
            onNext?.();
          } catch (err) {
            console.error("onNext callback failed", err);
          }
        }}
      />
    </div>
  );
}
